/**
 * TemplatePicker — "Choose your format": one card per template, with a peek at
 * its rules. Used when starting a rulebook and when creating an event.
 */
import { useEffect, useState } from 'react';
import { getTemplates } from './api';

const ICONS = {
  box_5_over: 'ph-lightning', league_6_over: 'ph-trophy', league_7_categories: 'ph-users-four',
  league_playoffs: 'ph-ranking', league_cups: 'ph-medal', blank: 'ph-note-blank',
};

export default function TemplatePicker({ value, onChange, compact = false }) {
  const [templates, setTemplates] = useState(null);
  const [peek, setPeek] = useState(null);

  useEffect(() => {
    getTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  if (!templates) return <div className="h-24 rounded-2xl bg-gray-100 animate-pulse" />;
  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`} data-testid="template-picker">
      {templates.map((t) => {
        const on = value === t.key;
        return (
          <div key={t.key}
            className={`rounded-2xl border-2 bg-white p-4 cursor-pointer transition-colors ${on ? 'border-[#2B3A67]' : 'border-gray-100 hover:border-[#2B3A67]/30'}`}
            onClick={() => onChange(on ? null : t.key)} data-testid={`template-${t.key}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${on ? 'bg-[#2B3A67] text-white' : 'bg-[#E9EBF1] text-[#2B3A67]'}`}>
                <i className={`ph-bold ${ICONS[t.key] || 'ph-book-open'} text-lg`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-[#2B3A67] leading-tight">{t.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.summary}</p>
                {t.rules.length > 0 && (
                  <button type="button" className="text-xs font-semibold text-[#1F7A75] mt-1.5"
                    onClick={(e) => { e.stopPropagation(); setPeek(peek === t.key ? null : t.key); }}>
                    {peek === t.key ? 'Hide rules' : `See ${t.rules.length} rules`}
                  </button>
                )}
              </div>
              {on && <i className="ph-fill ph-check-circle text-[#3AAFA9] text-xl" />}
            </div>
            {peek === t.key && (
              <ul className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
                {t.rules.map((r, i) => (
                  <li key={i} className="text-xs text-gray-600 flex gap-2"><span className="text-[#3AAFA9]">•</span>{r.sentence}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
